// Utilities & Hooks
import { useEffect, useState } from "react";
import { useChatGetMessages, useChatInitializeNewConversation } from "../../../api/Chat/Chat";
import { groupBy } from "lodash-es";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../providers/auth-context";
import { ChatMessagesResponseFields, ChatResponseFields } from "../../../api/Chat/interfaces";
import { useChatsContext } from "../ChatWrapper/ChatContextWrapper";
import useErrorReporting from "../../../hooks/useErrorReporting";

// Components
import CustomScrollbars from "../../CustomScrollbars/CustomScrollbars";
import ChatConversationMessageGroup from "../Conversation/ChatConversationMessageGroup";
import ChatConversationForm from "../Conversation/ChatConversationForm";
import ChatConversationMessageSkeleton from "../Conversation/ChatConversationMessageSkeleton";
import ChatPanelFirstMessageInfo from "./ChatPanelFirstMessageInfo";

// Interfaces
import {
  ChatConversationMessageGroups,
  ChatPanelConversationProps,
} from "../Conversation/interfaces";

// Constants
import { CHATS_MESSAGE_SKELETON_PLACEHOLDERS } from "../constants";

// Socket
import socket from "../../../config/chat-socket";

const ChatPanelConversation = ({
  details,
  chatConversationDetails,
  modifierClass = "",
}: ChatPanelConversationProps) => {
  const queryClient = useQueryClient();
  const chatsContext = useChatsContext();
  const { user, handleUpdateUserDetails } = useAuth();
  const errorReporting = useErrorReporting();

  /*=======================
    ID OF CURRENT CHAT
  ========================*/
  const [chatID, setChatID] = useState<number | null>(details.chat_id);

  /*================================
    FETCH ALL THE MESSAGES FOR
    THE TARGETED CHAT
  =================================*/
  const { data, isFetching, isPending, isRefetching } = useChatGetMessages(chatID);

  /*================================
    MAP RECEIVED CHAT MESSAGES DATA
  =================================*/
  const [chatMessages, setChatMessages] = useState<ChatResponseFields[]>([]);
  const [chatMessagesUI, setChatMessagesUI] = useState<ChatConversationMessageGroups[]>([]);

  // When the component mounts, read the existing chat data for it,
  // and if there are any messages for it use them to populate the
  // messages that are displayed in the UI
  useEffect(() => {
    const existingChatData = queryClient.getQueryData([
      "sms-chat-messages",
      chatID,
    ]) as ChatMessagesResponseFields;

    // If there's no data for the chat, exit function preventing trying to map over non-existing data
    if (
      !existingChatData ||
      !Object.entries(existingChatData).length ||
      !existingChatData.messages.length ||
      isRefetching
    ) {
      return;
    }

    // Group the messages by date
    // TODO: Maybe extract this in a separate function
    const groupedMessages = groupBy(existingChatData.messages, message => {
      const formattedMessageDate: string = format(
        new Date(message.timestamp * 1000),
        "MMM dd yyyy",
      );
      return formattedMessageDate;
    });

    // If there are no groups of messages, exit function
    if (!Object.entries(groupedMessages).length) return;

    // Convert the grouped messages object to an ittreable array
    // in reverse order, meaning the latest messages will be
    // shown at the bottom of the conversation
    const arrayOfGroupedMessages = Object.entries(groupedMessages).map(message => {
      const [key, value] = message;
      return { groupDate: key, groupMessages: value };
    });

    setChatMessagesUI(arrayOfGroupedMessages);
  }, [chatID]);

  // When the data from the API for the messages that belong to a
  // specific chat are fetched, update the messages that are displayed in the UI
  useEffect(() => {
    if (!data || !data.messages || !data.messages.length) return;
    setChatMessages(data.messages);
  }, [data]);

  // When the list of chat messages is updated after a message was sent
  // when working with the socket communication, update the messages that are
  // displayed in the UI
  useEffect(() => {
    if (!chatMessages.length) return;

    // Group the messages by date
    const groupedMessages = groupBy(chatMessages, message => {
      const formattedMessageDate: string = format(
        new Date(message.timestamp * 1000),
        "MMM dd yyyy",
      );
      return formattedMessageDate;
    });

    // If there are no groups of messages, exit function
    if (!Object.entries(groupedMessages).length) return;

    // Convert the grouped messages object to an ittreable array
    // in reverse order, meaning the latest messages will be
    // shown at the bottom of the conversation
    const arrayOfGroupedMessages = Object.entries(groupedMessages).map(message => {
      const [key, value] = message;
      return { groupDate: key, groupMessages: value };
    });

    setChatMessagesUI(arrayOfGroupedMessages);
  }, [chatMessages]);

  /*=====================================
    SEND MESSAGE AND INITIALIZE NEW CHAT
  ======================================*/
  const chatInitializeNew = useChatInitializeNewConversation();
  const handleChatInitializeNewConversation = async (message: string) => {
    if (!message) return;

    const TEMPORARY_MESSAGE_FOR_UI: ChatResponseFields = {
      id: null,
      chat_id: null,
      direction: "out",
      content: message,
      is_read: 1,
      timestamp: new Date().getTime() / 1000,
      status: "message-sending",
    };

    try {
      // Update the UI directly
      const updatedMessages = [...chatMessages];
      updatedMessages.push(TEMPORARY_MESSAGE_FOR_UI);
      setChatMessages(updatedMessages);

      await chatInitializeNew.mutateAsync({
        applicant_id: details.applicant.id,
        application_id: details.application_id as number,
        message,
      });
    } catch (error) {
      setChatMessages([{ ...TEMPORARY_MESSAGE_FOR_UI, status: "message-failed" }]);
      errorReporting("Failed initializing new chat conversation", error, {
        applicant_id: details.applicant.id,
        application_id: details.application_id as number,
      });
    }
  };

  // Update the displayed chat messages data
  useEffect(() => {
    if (!chatInitializeNew.data) return;

    // Update the chat messages data for the chat that we're viewing at the moment
    const chatData = chatInitializeNew.data;
    queryClient.setQueryData(["sms-chat-messages", chatData.id], chatData);
    setChatID(chatData.id);

    // The formatted dataset that will be appended to the list
    // of existing user chats, directly in the cached query data
    const CHAT_DATA_CACHE = {
      id: chatData.id,
      is_blocked: chatData.is_blocked,
      user_id: chatData.user_id,
      applicant: chatData.applicant,
      last_message: chatData.messages[0],
    };

    // Update the list of existing user chats that are
    // saved in the cached react query data
    const updatedList = chatsContext.existingUserChats ? [...chatsContext.existingUserChats] : [];
    updatedList.unshift(CHAT_DATA_CACHE);
    queryClient.setQueryData(["sms-chats", user.id], updatedList);
  }, [chatInitializeNew.data]);

  /*=====================================
    SEND A MESSAGE TO THE SOCKET
  ======================================*/
  const handleChatSendNewMessage = (message: string) => {
    if (!message) return;

    // Update the UI with the message that we just sent
    const SENT_MESSAGE_TIMESTAMP = new Date().getTime() / 1000;

    const SENT_MESSAGE: ChatResponseFields = {
      id: `${chatID}_${chatMessages.length + 1}`,
      chat_id: chatID,
      direction: "out",
      content: message,
      is_read: 1,
      timestamp: SENT_MESSAGE_TIMESTAMP,
      status: user.bandwidth_number_status === "unavailable" ? "message-sending" : null,
    };

    // Update the UI state for the displayed chat messages
    const outboundMessages = [...chatMessages];
    outboundMessages.push(SENT_MESSAGE);
    setChatMessages(outboundMessages);

    // Emit event to the socket with the newly sent message
    socket.emit("outbound message", {
      chat_id: chatID,
      content: message,
      temporary_id: SENT_MESSAGE.id,
    });

    // Block all open chat panels temporarily from being able to send a new message
    // until a new number from Bandwidth has been issued
    if (user.bandwidth_number_status === "unavailable") {
      handleUpdateUserDetails({ bandwidth_number_status: "pending" });
    }

    // After successfully sending a message to an existing chat,
    // update that chat's last message properties that are displayed
    // in the chat messages dropdown menu
    if (!chatsContext.existingUserChats) return;

    const updatedExistingUserChats = [...chatsContext.existingUserChats];

    // Find the matching chat, move it to the start of the list and update it's last message value
    const matchingChatIndex: number = updatedExistingUserChats.findIndex(existingChat => {
      return existingChat.id === chatID;
    });

    // Exit if chat cannot be found
    if (matchingChatIndex < 0) return;

    // Update the last message value
    updatedExistingUserChats.splice(matchingChatIndex, 1, {
      ...updatedExistingUserChats[matchingChatIndex],
      last_message: {
        ...updatedExistingUserChats[matchingChatIndex].last_message,
        content: message,
        timestamp: SENT_MESSAGE_TIMESTAMP,
      },
    });

    // Update the cached query data
    queryClient.setQueryData(["sms-chats", user.id], updatedExistingUserChats);
  };

  socket.on("inbound message", (incomingMessage: ChatResponseFields) => {
    // Prevent state updates if the chat is still marked as new
    if (details.is_new_chat) return;

    // Prevent state updates if the ID of the chat to which the message belongs to
    // does not corresponds to the currently opened chat
    if (incomingMessage.chat_id !== chatID) return;

    // Update the state with all the incoming messages
    const incomingMessages = [...chatMessages];
    incomingMessages.push(incomingMessage);
    setChatMessages(incomingMessages);
  });

  /*=====================================
    MESSAGE RECEIVED & STATUS EVENTS
  ======================================*/
  socket.on("message_received", messageDetails => {
    // If the message does not belong to this chat, exit
    if (messageDetails.chat_id !== chatID) return;

    // Find the message from the list of already
    // present messages that should be updated in the UI
    const updatedMessages = [...chatMessages];
    const updatedMessageIndex: number = updatedMessages.findIndex(message => {
      return message.id === messageDetails.temporary_id;
    });

    // If the message cannot be found, exit
    if (updatedMessageIndex < 0) return;

    // Update the "id" property of the targeted message,
    // from its "temporary" value to the "real" value received from the server
    updatedMessages.splice(updatedMessageIndex, 1, {
      ...updatedMessages[updatedMessageIndex],
      id: messageDetails.id,
    });

    setChatMessages(updatedMessages);
  });

  socket.on("message_status", messageDetails => {
    // If the message does not belong to this chat, exit
    if (messageDetails.chat_id !== chatID) return;

    // Find the message from the list of already
    // present messages that should be updated in the UI
    const updatedMessages = [...chatMessages];
    const updatedMessageIndex: number = updatedMessages.findIndex(message => {
      return message.id === messageDetails.message_id;
    });

    // If the message cannot be found, exit
    if (updatedMessageIndex < 0) return;

    // Update the "status" property of the targeted message
    updatedMessages.splice(updatedMessageIndex, 1, {
      ...updatedMessages[updatedMessageIndex],
      status: messageDetails.status,
    });

    setChatMessages(updatedMessages);

    // Re-enable all open chats to be able to send a new message once
    // new number from Bandwidth has been issued successfully
    if (user.bandwidth_number_status === "pending") {
      handleUpdateUserDetails({ bandwidth_number_status: "available" });
    }
  });

  return (
    <div className={`chat-conversation chat-conversation--location--panel ${modifierClass}`}>
      <div className="chat-conversation__messages">
        <CustomScrollbars
          maxHeight="260px"
          autoScrollToBottom={{ enabled: true, updateTrigger: chatMessagesUI }}
        >
          {isFetching && isPending ? (
            CHATS_MESSAGE_SKELETON_PLACEHOLDERS.map(placeholder => (
              <ChatConversationMessageSkeleton chatLocation="panel" direction={placeholder} />
            ))
          ) : chatMessages.length === 0 && user.bandwidth_number_status === "unavailable" ? (
            <ChatPanelFirstMessageInfo />
          ) : (
            chatMessagesUI.map(group => (
              <ChatConversationMessageGroup
                key={group.groupDate}
                groupDate={group.groupDate}
                groupMessages={group.groupMessages}
                chatConversationDetails={chatConversationDetails}
              />
            ))
          )}
        </CustomScrollbars>
      </div>

      <ChatConversationForm
        id={details.id}
        chatLocation="panel"
        handleSendChatMessage={(message: string) => {
          if (details.is_new_chat) {
            handleChatInitializeNewConversation(message);
          } else {
            handleChatSendNewMessage(message);
          }
        }}
        bandwidthNumberStatus={user.bandwidth_number_status}
      />
    </div>
  );
};

export default ChatPanelConversation;
